import { isEqual } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { ColumnDensity, SORT_ORDER } from '@common/types';

export type PersistedPartnerColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialPartnerColumns: PersistedPartnerColumn[] = [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 220,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'practice_count',
    headerName: 'Practices',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
  },
  {
    field: 'operator_count',
    headerName: 'Operators',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
  },
  {
    field: 'care_navigator_count',
    headerName: 'Care Navigators',
    width: 150,
    minWidth: 150,
    sortable: true,
    filterable: false,
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'county',
    headerName: 'County',
    minWidth: 140,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'admin_count',
    headerName: 'Admins',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
  },
];

export const initialPartnerPinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

export type ColumnNames =
  | 'name'
  | 'practice_count'
  | 'operator_count'
  | 'care_navigator_count'
  | 'city'
  | 'county'
  | 'admin_count';

export interface IPartnerTableFilters {
  search: string;
  sort_by: ColumnNames;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
}

const getDefaultFilters = (): IPartnerTableFilters => ({
  search: '',
  sort_order: 'asc',
  sort_by: 'name',
  page: 1,
  limit: 10,
});

type TKeyOfFilters = keyof IPartnerTableFilters;

const getAppliedFiltersCount = (
  initialFiltersList: IPartnerTableFilters,
  appliedFilters: IPartnerTableFilters,
): number => {
  const filtersKeyList: TKeyOfFilters[] = Object.keys(appliedFilters).filter(
    key =>
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'search' &&
      key !== 'sort_order' &&
      key !== 'sort_by',
  ) as TKeyOfFilters[];

  return filtersKeyList.reduce(
    (count, key) =>
      isEqual(initialFiltersList[key], appliedFilters[key]) ? count : count + 1,
    0,
  );
};

type TPartnerTableStore = {
  filters: IPartnerTableFilters;
  setFilters: (newFilters: Partial<IPartnerTableFilters>) => void;
  clearStore: () => void;
  appliedFiltersCount: number;

  columns: PersistedPartnerColumn[];
  setColumns: (columns: PersistedPartnerColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  resetView: () => void;
};

export const usePartnerTableStore = create<TPartnerTableStore>()(
  persist(
    set => ({
      filters: getDefaultFilters(),
      setFilters: newFilters =>
        set(state => {
          const updatedFilters = { ...state.filters, ...newFilters };
          return {
            filters: updatedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedFilters,
            ),
          };
        }),
      clearStore: () =>
        set(() => ({
          filters: getDefaultFilters(),
          appliedFiltersCount: 0,
        })),
      appliedFiltersCount: 0,

      columns: initialPartnerColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialPartnerPinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      hiddenColumns: [],
      setHiddenColumns: hiddenColumns => set({ hiddenColumns }),
      density: 'comfortable',
      setDensity: density => set({ density }),
      resetView: () =>
        set({
          columns: initialPartnerColumns,
          pinnedColumns: initialPartnerPinnedColumns,
          hiddenColumns: [],
          density: 'comfortable',
        }),
    }),
    {
      name: 'partner-table-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
        hiddenColumns: state.hiddenColumns,
        density: state.density,
        sort_by: state.filters.sort_by,
        sort_order: state.filters.sort_order,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<
          TPartnerTableStore &
            Pick<IPartnerTableFilters, 'sort_by' | 'sort_order'>
        >;

        const mergedFilters: IPartnerTableFilters = {
          ...currentState.filters,
          ...(persisted.sort_by && { sort_by: persisted.sort_by }),
          ...(persisted.sort_order && { sort_order: persisted.sort_order }),
        };

        return {
          ...currentState,
          ...persisted,
          filters: mergedFilters,
        };
      },
    },
  ),
);
